// Use environment variable for API base URL in production, fallback to /api for dev proxy
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

async function fetchApi(endpoint, options = {}, signal = null) {
  const fetchOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  };

  // Add abort signal if provided
  if (signal) {
    fetchOptions.signal = signal;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, fetchOptions);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `API error: ${response.status}`);
  }

  return response.json();
}

/**
 * Download file from API endpoint
 */
async function downloadFile(endpoint) {
  const response = await fetch(`${API_BASE}${endpoint}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || `API error: ${response.status}`);
  }

  // Get filename from Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'download';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = decodeURIComponent(match[1]);
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Get available containers (years) from blob storage
 * @returns {Promise<{ containers: string[], count: number }>}
 */
export async function getContainers() {
  return fetchApi('/emails/containers');
}

/**
 * Get emails from blob storage
 * @param {object} params - { container, prefix, page, limit, hasAttachments, sender, folder }
 */
export async function getBlobEmails(params = {}) {
  const query = new URLSearchParams(params).toString();
  return fetchApi(`/emails/blob/list${query ? `?${query}` : ''}`);
}

/**
 * Get a single email from blob storage
 * @param {object} params - { container, path }
 */
export async function getBlobEmail({ container, path }) {
  const params = new URLSearchParams();
  if (container) params.set('container', container);
  params.set('path', path);
  return fetchApi(`/emails/blob?${params.toString()}`);
}

/**
 * Search emails in blob storage
 * @param {object} params - { q, container, prefix, limit }
 */
export async function searchBlobEmails(params = {}) {
  const query = new URLSearchParams(params).toString();
  return fetchApi(`/emails/search?${query}`);
}

/**
 * Browse emails with consolidated endpoint (OPTIMIZED)
 * Single API call loads all quarters in parallel on the server
 * @param {object} params - { container, quarters, page, limit, sender, folder, hasAttachments }
 * @param {AbortSignal} signal - Optional abort signal to cancel the request
 */
export async function browseBlobEmails(params = {}, signal = null) {
  const query = new URLSearchParams(params).toString();
  return fetchApi(`/emails/blob/browse${query ? `?${query}` : ''}`, {}, signal);
}

/**
 * Search emails across multiple containers/quarters (OPTIMIZED)
 * Uses Azure Cognitive Search when available, falls back to blob scanning
 * @param {object} params - { q, container, limit, page, folder, hasAttachments, senderEmail, dateFrom, dateTo }
 * @param {AbortSignal} signal - Optional abort signal to cancel the request
 */
export async function searchAllEmails(params = {}, signal = null) {
  const query = new URLSearchParams(params).toString();
  return fetchApi(`/emails/search/all?${query}`, {}, signal);
}

/**
 * Get search suggestions (autocomplete)
 * @param {string} query - Partial search query
 * @param {string} container - Optional container filter
 */
export async function getSearchSuggestions(query, container = null) {
  const params = new URLSearchParams({ q: query });
  if (container) params.set('container', container);
  return fetchApi(`/emails/search/suggest?${params.toString()}`);
}

/**
 * Get search index statistics
 */
export async function getSearchStats() {
  return fetchApi('/emails/search/stats');
}

/**
 * Forward email - downloads EML file
 * @param {string} id - Email ID
 * @param {object} blobInfo - { container, path }
 */
export async function forwardEmail(id, blobInfo = null) {
  let endpoint = `/emails/${encodeURIComponent(id)}/forward`;
  if (blobInfo?.container && blobInfo?.path) {
    const params = new URLSearchParams();
    params.set('container', blobInfo.container);
    params.set('blobPath', blobInfo.path);
    endpoint += `?${params.toString()}`;
  }
  return downloadFile(endpoint);
}

/**
 * Download an attachment
 * @param {string} emailId - Email ID
 * @param {string} attachmentName - Name of the attachment
 * @param {object} blobInfo - { container, path }
 * @param {string} attachmentBlobPath - Direct blob path to the attachment (from email metadata)
 */
export async function downloadAttachment(emailId, attachmentName, blobInfo = null, attachmentBlobPath = null) {
  let endpoint = `/attachments/${encodeURIComponent(emailId)}/${encodeURIComponent(attachmentName)}`;
  const params = new URLSearchParams();
  if (blobInfo?.container) {
    params.set('container', blobInfo.container);
  }
  if (blobInfo?.path) {
    params.set('blobPath', blobInfo.path);
  }
  if (attachmentBlobPath) {
    params.set('attachmentPath', attachmentBlobPath);
  }
  if (params.toString()) {
    endpoint += `?${params.toString()}`;
  }
  return downloadFile(endpoint);
}

/**
 * Create draft email in Outlook via Microsoft Graph API
 * @param {string} id - Email ID
 * @param {string} accessToken - Microsoft Graph access token
 * @param {object} blobInfo - { container, path }
 */
export async function createOutlookDraft(id, accessToken, blobInfo = null) {
  let endpoint = `${API_BASE}/emails/${encodeURIComponent(id)}/create-draft`;
  if (blobInfo?.container && blobInfo?.path) {
    const params = new URLSearchParams();
    params.set('container', blobInfo.container);
    params.set('blobPath', blobInfo.path);
    endpoint += `?${params.toString()}`;
  }
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.error?.message || `API error: ${response.status}`);
    err.code = error.error?.code;
    err.status = response.status;
    throw err;
  }

  return response.json();
}

// Legacy exports for backwards compatibility
export const getEmails = getBlobEmails;
export const getEmail = (id) => fetchApi(`/emails/${encodeURIComponent(id)}`);
export const searchEmails = searchBlobEmails;
export const getStats = () => fetchApi('/stats');
export const getIndexStatus = () => fetchApi('/stats/index');
